import * as React from 'react';
import { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { ArrowLeft, Loader2 } from 'lucide-react';
import { API, Storage } from 'aws-amplify';
import { format, parse } from 'date-fns';
import { PageHeader } from '../../components/page-header';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { useToast } from '../../components/ui/toast';

declare const global: {
  me: {
    imageName?: string;
    name: string;
  };
};

interface RewardState {
  id: string;
  name: string;
  imageName: string;
  imageType: string;
  cost: number;
  limitCount: number;
  remainingCount: number;
  description: string;
  validTo: string;
  returnTo?: string;
}

export function RewardDetailsPage() {
  const { state: reward } = useLocation() as { state: RewardState };
  const navigate = useNavigate();
  const toast = useToast();
  const [imageUrl, setImageUrl] = useState<string | null>(null);
  const [isBuying, setIsBuying] = useState(false);

  const isSoldOut = reward?.remainingCount <= 0;
  const returnPath = reward?.returnTo || '/v2/performance/rewards';

  useEffect(() => {
    async function loadImage() {
      if (reward?.imageName && reward.imageName !== '') {
        if (
          reward.imageType === 'gif_url' ||
          reward.imageType === 'giftcard_url' ||
          reward.imageType === 'trophy'
        ) {
          setImageUrl(reward.imageName);
        } else {
          const url = await Storage.get(reward.imageName);
          setImageUrl(url as string);
        }
      }
    }
    loadImage();
  }, [reward?.imageName, reward?.imageType]);

  if (!reward) {
    navigate('/v2/performance/rewards');
    return null;
  }

  async function handleBuy() {
    if (isSoldOut || isBuying) return;
    setIsBuying(true);

    try {
      const acceptRewardRes: any = await API.graphql({
        query: acceptRewardMutation,
        variables: { rewardID: reward.id, action: 'buy' },
      });
      const res = JSON.parse(acceptRewardRes?.data?.acceptReward);

      if (res.errorMessage === 'success') {
        toast.success('Reward Redeemed');
        navigate(returnPath);
      } else {
        if (res.errorMessage === 'RewardLimitExhausted') {
          toast.error('Sorry, this reward is sold out.');
        } else if (res.errorMessage === 'UserLimitExhausted') {
          toast.error(
            'Sorry, you have already purchased your limit of this reward.'
          );
        } else if (res.errorMessage === 'NotEnoughCirclePoints') {
          toast.error(
            'Sorry, you do not have enough points to purchase this reward.'
          );
        } else if (res.errorMessage === 'NotEnoughOrgPoints') {
          toast.error(
            'Sorry, you do not have enough points to purchase this reward.'
          );
        } else {
          toast.error(
            'Sorry, we ran into an error. Gamify support has been notified.'
          );
        }
      }
    } catch (err) {
      toast.error('Sorry, we ran into an error. Please try again.');
    } finally {
      setIsBuying(false);
    }
  }

  return (
    <div className="h-full w-full flex flex-col overflow-hidden">
      <PageHeader
        title="Reward Details"
        avatar={{
          src: global.me?.imageName || undefined,
          name: global.me?.name,
        }}
      />

      <div className="flex-1 overflow-auto px-6 py-6">
        {/* Back button */}
        <button
          onClick={() => navigate(returnPath)}
          className="flex items-center gap-2 text-sm font-bold text-foreground hover:text-primary transition-colors mb-8"
        >
          <ArrowLeft className="size-4" />
        </button>

        {/* Content */}
        <div className="flex flex-col md:flex-row justify-center gap-8 max-w-4xl mx-auto">
          {/* Image */}
          <div className="flex-shrink-0">
            {imageUrl ? (
              <img
                src={imageUrl}
                alt={reward.name}
                className="w-full md:w-[400px] lg:w-[500px] h-[300px] md:h-[400px] lg:h-[500px] object-contain rounded-lg border border-border"
              />
            ) : (
              <div className="w-full md:w-[300px] h-[300px] rounded-lg border border-border flex items-center justify-center">
                <p className="text-sm font-bold text-muted-foreground">
                  No Image Available
                </p>
              </div>
            )}
          </div>

          {/* Details */}
          <div className="w-full md:w-[300px] space-y-3">
            <Badge variant="secondary" className="text-xs">
              {reward.remainingCount}/{reward.limitCount}
            </Badge>

            <h2 className="text-2xl md:text-[28px] font-bold text-foreground">
              {reward.name}
            </h2>

            <p className="text-sm text-muted-foreground">
              {reward.description}
            </p>

            <p className="text-sm font-semibold text-foreground">
              {formatPoints(reward.cost)}
            </p>

            <div className="pt-4">
              <Button
                onClick={handleBuy}
                disabled={isSoldOut || isBuying}
                className="w-full h-11 rounded-md font-semibold"
                variant={isSoldOut ? 'secondary' : 'default'}
              >
                {isBuying && <Loader2 className="size-4 animate-spin mr-2" />}
                {isBuying ? 'Processing...' : 'Buy now'}
              </Button>

              {isSoldOut && (
                <p className="text-sm font-bold text-destructive mt-2">
                  Sold out
                </p>
              )}
            </div>

            <p className="text-sm text-muted-foreground pt-2">
              Valid until{' '}
              {format(
                parse(reward.validTo, 'yyyy-MM-dd', new Date()),
                'MM/dd/yy'
              )}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}

function formatPoints(number: number) {
  if (number <= 0) return '';
  if (number === 1) {
    return number + ' point';
  } else {
    return number + ' points';
  }
}

const acceptRewardMutation = /* GraphQL */ `
  mutation AcceptReward($rewardID: String!, $action: String!) {
    acceptReward(rewardID: $rewardID, action: $action)
  }
`;
